import { useQuery } from '@tanstack/react-query';
import { AwardsService } from '../services';
import { awardsKeys } from '../queryKeys';

/**
 * The cycle's *unfiltered* membership count.
 *
 * Its only job is to tell CycleDetail whether an otherwise-locked complete
 * cycle is empty enough to rescue: awards-service (GOTU-5626) lets an admin
 * re-date a complete cycle that has no memberships, which is how a mistyped
 * end date gets corrected (GOTU-6393). The cycle payload doesn't carry a count,
 * and the roster's own `totalCount` reflects the active filters — a filter that
 * matches nothing would zero it and wrongly unlock Edit — so the emptiness
 * signal has to come from a filter-free read of its own.
 *
 * Memberships is the whole of the count deliberately: it mirrors the backend
 * gate, which counts memberships alone because that is currently the entire set
 * of cycle-scoped tables. When applications (GOTU-5628) or feedback (GOTU-5629)
 * land, a cycle can hold rows this misses; widen the signal with them then, as
 * the backend must.
 *
 * `enabled` lets the caller skip the request for pending/active cycles, whose
 * Edit control never consults this count.
 */
export const useCycleMembershipCount = (cycleId, { enabled = true } = {}) => {
  const { data, isPending, isError } = useQuery({
    queryKey: awardsKeys.memberships(cycleId, {}, 1),
    queryFn: () => AwardsService.getCycleMemberships(cycleId, {}, 1),
    enabled: Boolean(cycleId) && enabled,
    retry: false,
  });

  return {
    count: data?.totalCount ?? 0,
    isPending,
    isError,
  };
};
